import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';

import AddressAutocomplete from '@/components/address-autocomplete';

import type { CreateSavedAddressReq, SavedAddress } from '@/types/parcel/userData';

import useSavedAddressFormModal from './use-saved-address-form-modal';

interface SavedAddressFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateSavedAddressReq) => Promise<void>;
  initialAddress?: SavedAddress | null;
  defaultType?: 'origin' | 'destination' | 'both';
  defaultCountry?: string;
}

export const SavedAddressFormModal = (props: SavedAddressFormModalProps) => {
  const { open, onClose, onSave, initialAddress, defaultType, defaultCountry } = props;

  const hooks = useSavedAddressFormModal({
    initialAddress,
    defaultType,
    defaultCountry,
    onSave,
    onClose,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {hooks.isEditing ? 'Editar Dirección Guardada' : 'Nueva Dirección Guardada'}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={hooks.onSubmit} noValidate>
        <DialogContent dividers sx={{ p: 3 }}>
          <div className="space-y-4">
            {/* Identificación y Tipo */}
            <Typography
              className="pb-3"
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
            >
              Identificación
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xxs: 12, sm: 8 }}>
                <TextField
                  {...hooks.register('alias')}
                  label="Alias / Nombre de la ubicación *"
                  placeholder="Ej: Bodega Naucalpan, Sucursal Centro, Casa"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.alias}
                  helperText={hooks.errors.alias?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Tipo de Dirección *"
                  size="small"
                  fullWidth
                  value={hooks.watch('type') || 'both'}
                  onChange={(e) =>
                    hooks.setValue('type', e.target.value as 'origin' | 'destination' | 'both', {
                      shouldValidate: true,
                    })
                  }
                  error={!!hooks.errors.type}
                  helperText={hooks.errors.type?.message}
                >
                  <MenuItem value="both">Ambos (Origen y Destino)</MenuItem>
                  <MenuItem value="origin">Solo Origen (Recolección)</MenuItem>
                  <MenuItem value="destination">Solo Destino (Entrega)</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Búsqueda Geográfica con Google Places */}
            <Typography
              className="py-3"
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mt: 2 }}
            >
              Ubicación Geográfica (Google Maps)
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xxs: 12 }}>
                <AddressAutocomplete
                  id="saved-address-autocomplete"
                  size="small"
                  label="Buscar dirección *"
                  placeholder="Escribe calle, número, colonia o ciudad para autocompletar..."
                  value={hooks.watch('address') || ''}
                  country={hooks.userCountry}
                  onChange={hooks.onAddressChange}
                  onSelectAddress={hooks.onAddressSelect}
                  error={!!hooks.errors.address}
                  helperText={
                    hooks.errors.address?.message ||
                    'Selecciona una dirección de las sugerencias para obtener coordenadas exactas'
                  }
                  startAdornment={
                    <LocationOnOutlinedIcon
                      fontSize="small"
                      sx={{ color: 'primary.main', mr: 0.5 }}
                    />
                  }
                />
              </Grid>

              {/* Campos detallados de la dirección (autocompletados por el Geocoder) */}
              <Grid size={{ xxs: 6, sm: 3 }}>
                <TextField
                  {...hooks.register('street_number')}
                  label="Núm. Exterior"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.street_number}
                  helperText={hooks.errors.street_number?.message}
                />
              </Grid>
              <Grid size={{ xxs: 6, sm: 3 }}>
                <TextField
                  {...hooks.register('interior_number')}
                  label="Núm. Interior / Piso"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.interior_number}
                  helperText={hooks.errors.interior_number?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <TextField
                  {...hooks.register('colony')}
                  label="Colonia / Barrio"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.colony}
                  helperText={hooks.errors.colony?.message}
                />
              </Grid>
              <Grid size={{ xxs: 6, sm: 4 }}>
                <TextField
                  {...hooks.register('zip_code')}
                  label="Código Postal *"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.zip_code}
                  helperText={hooks.errors.zip_code?.message}
                />
              </Grid>
              <Grid size={{ xxs: 6, sm: 4 }}>
                <TextField
                  {...hooks.register('city')}
                  label="Ciudad / Municipio *"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.city}
                  helperText={hooks.errors.city?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 4 }}>
                <TextField
                  {...hooks.register('state')}
                  label="Estado / Región"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.state}
                  helperText={hooks.errors.state?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12 }}>
                <TextField
                  {...hooks.register('references')}
                  label="Referencias de entrega / recolección"
                  placeholder="Entre calles, color de fachada, portón o indicaciones..."
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  error={!!hooks.errors.references}
                  helperText={hooks.errors.references?.message}
                />
              </Grid>
            </Grid>

            {/* Datos de Contacto */}
            <Typography
              className="py-3"
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mt: 2 }}
            >
              Datos de Contacto
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <TextField
                  {...hooks.register('contact.first_name')}
                  label="Nombre de Contacto *"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.contact?.first_name}
                  helperText={hooks.errors.contact?.first_name?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <TextField
                  {...hooks.register('contact.last_name')}
                  label="Apellido de Contacto *"
                  size="small"
                  fullWidth
                  error={!!hooks.errors.contact?.last_name}
                  helperText={hooks.errors.contact?.last_name?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <MuiTelInput
                  value={hooks.watch('contact.phone') || ''}
                  onChange={(e) => hooks.handlePhoneChange(e.replace(/\s/g, ''))}
                  size="small"
                  fullWidth
                  label="Teléfono (con código de país) *"
                  onlyCountries={['MX', 'PE', 'CO', 'CL']}
                  error={!!hooks.errors.contact?.phone}
                  helperText={hooks.errors.contact?.phone?.message}
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <TextField
                  {...hooks.register('contact.email')}
                  label="Correo Electrónico *"
                  size="small"
                  fullWidth
                  type="email"
                  error={!!hooks.errors.contact?.email}
                  helperText={hooks.errors.contact?.email?.message}
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={hooks.submitting}>
            Cancelar
          </Button>
          <Button variant="contained" type="submit" disabled={hooks.submitting}>
            {hooks.submitting ? 'Guardando...' : hooks.isEditing ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SavedAddressFormModal;
